import React, { useMemo } from 'react';

import { useTimeSheetDays } from '../../../components/TimeSheet/DayCard/useTimeSheetDays';
import { mapToDayWithTimeEntries } from './logic/mapping';
import DayTimeEntriesSmart from './DayTimeEntries/DayTimeEntriesSmart';
import { EmployeeType, getReadonlyReasons } from './logic';
import { PureQueryOptions } from '@apollo/client';
import { useUserInfo } from '../../../context/UserInfo';
import { TimeSheetProps } from '../../../components/TimeSheet/DayCard/types';
import { CostCenter } from '../../../__generated__/graphql';
import { useRestrictFutureEntries } from '../../../hooks/timeTrackingSettings';
import { useTimeTrackingContext } from '../../../hooks/timeTrackingSettings/TimeTrackingContext';

export type CostCenterProps = Pick<CostCenter, 'Id' | 'Name'>;

type Props = {
  timeSheet: TimeSheetProps;
  costCenters: readonly CostCenterProps[];
  refetchQueriesForEmployeeMode?: PureQueryOptions[];
};

const TimeTrackingEntries: React.FC<Props> = ({
  timeSheet,
  costCenters,
  refetchQueriesForEmployeeMode,
}) => {
  const days = useTimeSheetDays(timeSheet);
  const userInfo = useUserInfo();
  const { isManagerView } = useTimeTrackingContext();
  const timeSheetEmployeeId = timeSheet.flair__Employee__c;

  const { restrictFutureEntriesForADay } = useRestrictFutureEntries();

  const employeeType: EmployeeType = useMemo(() => {
    if (isManagerView) {
      return 'manager';
    } else if (
      userInfo.canChangeOwnTimeEntries &&
      timeSheetEmployeeId === userInfo.id
    ) {
      return 'employeeWithCanChangeOwnTimeEntries';
    }
    return 'employee';
  }, [timeSheetEmployeeId, isManagerView, userInfo]);

  return (
    <>
      {days
        .map((x) => mapToDayWithTimeEntries(x, employeeType))
        .map((dayItem) => {
          const readonlyReasons = getReadonlyReasons(
            timeSheet,
            dayItem.timeEntries,
            dayItem.day.toDate(),
            restrictFutureEntriesForADay,
          );
          return (
            <DayTimeEntriesSmart
              key={dayItem.day.toISOString()}
              costCenters={costCenters}
              day={dayItem}
              readonlyReasons={readonlyReasons}
              refetchQueriesForEmployeeMode={refetchQueriesForEmployeeMode}
            />
          );
        })}
    </>
  );
};

export default TimeTrackingEntries;
